import React, { useCallback, useEffect, useMemo, useState } from "react";
import { usePersistentState } from "../Common/usePersistentFilters";
import {
 Badge,
 Button,
 Card,
 Col,
 Form,
 Modal,
 Row,
 Spinner,
 Table,
} from "react-bootstrap";
import { useSelector } from "react-redux";
import swal from "sweetalert";
import api from "../../../api";
import PageTitle from "../../layouts/PageTitle";

const CATEGORIES = [
 "Design",
 "Procurement",
 "Execution",
 "Commercial",
 "Safety",
 "Quality",
 "Client/PMC",
 "Regulatory",
 "Other",
];

const STATUSES = ["Open", "Mitigation In Progress", "Escalated", "Closed", "On Hold"];
const SEVERITIES = ["Low", "Medium", "High", "Critical"];

const severityVariant = {
 Low: "success",
 Medium: "warning",
 High: "danger",
 Critical: "dark",
};

const emptyEscalationMatrix = () => [
 { level: "Level 1", trigger: "Medium risk or unresolved action", ownerName: "", ownerEmail: "", responseTimeHours: 24, actionRequired: "" },
 { level: "Level 2", trigger: "High risk or missed Level 1 response", ownerName: "", ownerEmail: "", responseTimeHours: 48, actionRequired: "" },
 { level: "Level 3", trigger: "Critical risk or commercial/schedule impact", ownerName: "", ownerEmail: "", responseTimeHours: 72, actionRequired: "" },
 { level: "Client/PMC", trigger: "Client/PMC decision or parameter required", ownerName: "", ownerEmail: "", responseTimeHours: 0, actionRequired: "" },
];

const emptyForm = () => ({
 title: "",
 description: "",
 category: "Execution",
 probability: 3,
 impact: 3,
 status: "Open",
 owner: "",
 targetClosureDate: "",
 mitigationPlan: "",
 contingencyPlan: "",
 clientPmcParametersRequired: true,
 clientPmcParameters: {
 acceptanceCriteria: "",
 escalationThreshold: "",
 responseTat: "",
 approvalAuthority: "",
 communicationProtocol: "",
 remarks: "",
 },
 escalationMatrix: emptyEscalationMatrix(),
});

const toInputDate = (value) => {
 if (!value) return "";
 const date = new Date(value);
 return Number.isNaN(date.getTime()) ? "" : date.toISOString().slice(0, 10);
};

const fmtDate = (value) => (value ? new Date(value).toLocaleDateString("en-IN") : "-");

const SummaryCard = ({ label, value, variant }) => (
 <Col xs={6} md={3} xl={2}>
 <Card className="border-0 shadow-sm h-100" style={{ borderRadius: 8 }}>
 <Card.Body className="py-3">
 <div className="text-muted small">{label}</div>
 <h4 className={`mb-0 text-${variant}`}>{value || 0}</h4>
 </Card.Body>
 </Card>
 </Col>
);

const RiskRegister = () => {
 const user = useSelector((s) => s.auth?.userDetails || {});
 const isAdmin = (user.role?.name || user.roleName || "").toLowerCase() === "admin";
 const canManage =
 isAdmin ||
 (user.permissionNames || []).some((p) => ["create:projects", "edit:projects", "delete:projects"].includes(p.toLowerCase()));

 const [projects, setProjects] = useState([]);
 const [users, setUsers] = useState([]);
 const [risks, setRisks] = useState([]);
 const [summary, setSummary] = useState(null);
 const [selectedProject, setSelectedProject] = usePersistentState("risk_project", "");
 const [filterStatus, setFilterStatus] = usePersistentState("risk_status", "");
 const [filterSeverity, setFilterSeverity] = usePersistentState("risk_sev", "");
 const [filterCategory, setFilterCategory] = usePersistentState("risk_cat", "");
 const [searchText, setSearchText] = usePersistentState("risk_search", "");
 const [loading, setLoading] = useState(false);
 const [saving, setSaving] = useState(false);
 const [showModal, setShowModal] = useState(false);
 const [editingId, setEditingId] = useState(null);
 const [form, setForm] = useState(emptyForm());

 const loadProjects = useCallback(async () => {
 try {
 const res = await api.get("/projects?limit=500");
 if (res.data?.success) setProjects(res.data.data || []);
 } catch {
 setProjects([]);
 }
 }, []);

 const loadUsers = useCallback(async () => {
 try {
 const res = await api.get("/users?limit=200");
 if (res.data?.success) setUsers(res.data.data || []);
 } catch {
 setUsers([]);
 }
 }, []);

 const loadRisks = useCallback(async () => {
 setLoading(true);
 try {
 const params = {};
 if (selectedProject) params.project = selectedProject;
 if (filterStatus) params.status = filterStatus;
 if (filterSeverity) params.severity = filterSeverity;
 if (filterCategory) params.category = filterCategory;
 if (searchText) params.search = searchText;

 const [listRes, summaryRes] = await Promise.all([
 api.get("/risk-register", { params }),
 api.get("/risk-register/summary", { params: selectedProject ? { project: selectedProject } : {} }),
 ]);
 setRisks(listRes.data?.data || []);
 setSummary(summaryRes.data?.data || null);
 } catch (err) {
 swal("Error", err.response?.data?.message || "Failed to load risk register", "error");
 } finally {
 setLoading(false);
 }
 }, [selectedProject, filterStatus, filterSeverity, filterCategory, searchText]);

 useEffect(() => {
 loadProjects();
 loadUsers();
 }, [loadProjects, loadUsers]);

 useEffect(() => {
 loadRisks();
 }, [loadRisks]);

 const selectedProjectName = useMemo(
 () => projects.find((p) => p._id === selectedProject)?.projectName || "",
 [projects, selectedProject]
 );

 const setF = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));
 const setClientParam = (key, value) =>
 setForm((prev) => ({
 ...prev,
 clientPmcParameters: { ...prev.clientPmcParameters, [key]: value },
 }));

 const setEscalation = (index, key, value) =>
 setForm((prev) => ({
 ...prev,
 escalationMatrix: prev.escalationMatrix.map((item, i) =>
 i === index ? { ...item, [key]: value } : item
 ),
 }));

 const openCreate = () => {
 if (!selectedProject) {
 swal("Select Project", "Please select a project before adding a risk.", "warning");
 return;
 }
 setEditingId(null);
 setForm(emptyForm());
 setShowModal(true);
 };

 const openEdit = (risk) => {
 setEditingId(risk._id);
 setForm({
 title: risk.title || "",
 description: risk.description || "",
 category: risk.category || "Execution",
 probability: risk.probability || 3,
 impact: risk.impact || 3,
 status: risk.status || "Open",
 owner: risk.owner?._id || risk.owner || "",
 targetClosureDate: toInputDate(risk.targetClosureDate),
 mitigationPlan: risk.mitigationPlan || "",
 contingencyPlan: risk.contingencyPlan || "",
 clientPmcParametersRequired: risk.clientPmcParametersRequired ?? true,
 clientPmcParameters: {
 ...emptyForm().clientPmcParameters,
 ...(risk.clientPmcParameters || {}),
 },
 escalationMatrix: risk.escalationMatrix?.length ? risk.escalationMatrix : emptyEscalationMatrix(),
 });
 setShowModal(true);
 };

 const handleSave = async () => {
 if (!form.title.trim()) {
 swal("Validation", "Risk title is required", "warning");
 return;
 }

 setSaving(true);
 try {
 const payload = {
 ...form,
 project: selectedProject,
 owner: form.owner || null,
 targetClosureDate: form.targetClosureDate || null,
 probability: Number(form.probability) || 1,
 impact: Number(form.impact) || 1,
 escalationMatrix: form.escalationMatrix.map((item) => ({
 ...item,
 responseTimeHours: Number(item.responseTimeHours) || 0,
 })),
 };

 if (editingId) await api.patch(`/risk-register/${editingId}`, payload);
 else await api.post("/risk-register", payload);

 setShowModal(false);
 await loadRisks();
 swal("Success", "Risk register entry saved", "success");
 } catch (err) {
 swal("Error", err.response?.data?.message || "Failed to save risk entry", "error");
 } finally {
 setSaving(false);
 }
 };

 const handleDelete = async (risk) => {
 const ok = await swal({
 title: "Delete risk entry?",
 text: risk.title,
 icon: "warning",
 buttons: true,
 dangerMode: true,
 });
 if (!ok) return;

 try {
 await api.delete(`/risk-register/${risk._id}`);
 await loadRisks();
 swal("Deleted", "Risk entry deleted", "success");
 } catch (err) {
 swal("Error", err.response?.data?.message || "Failed to delete risk entry", "error");
 }
 };

 const scorePreview = Number(form.probability || 0) * Number(form.impact || 0);

 return (
 <>
 <PageTitle activeMenu="Risk Register" motherMenu="Projects" />

 <Row className="g-3 mb-3">
 <SummaryCard label="Low" value={summary?.severity?.Low} variant="success" />
 <SummaryCard label="Medium" value={summary?.severity?.Medium} variant="warning" />
 <SummaryCard label="High" value={summary?.severity?.High} variant="danger" />
 <SummaryCard label="Critical" value={summary?.severity?.Critical} variant="dark" />
 <SummaryCard label="Open" value={summary?.status?.Open} variant="primary" />
 <SummaryCard label="Client/PMC Pending" value={summary?.parametersPending} variant="info" />
 </Row>

 <Card className="border-0 shadow-sm" style={{ borderRadius: 8 }}>
 <Card.Body>
 <Row className="g-2 align-items-end mb-3">
 <Col md={3}>
 <Form.Label>Project</Form.Label>
 <Form.Select value={selectedProject} onChange={(e) => setSelectedProject(e.target.value)}>
 <option value="">All assigned projects</option>
 {projects.map((project) => (
 <option key={project._id} value={project._id}>{project.projectName}</option>
 ))}
 </Form.Select>
 </Col>
 <Col md={2}>
 <Form.Label>Status</Form.Label>
 <Form.Select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
 <option value="">All</option>
 {STATUSES.map((item) => <option key={item}>{item}</option>)}
 </Form.Select>
 </Col>
 <Col md={2}>
 <Form.Label>Severity</Form.Label>
 <Form.Select value={filterSeverity} onChange={(e) => setFilterSeverity(e.target.value)}>
 <option value="">All</option>
 {SEVERITIES.map((item) => <option key={item}>{item}</option>)}
 </Form.Select>
 </Col>
 <Col md={2}>
 <Form.Label>Category</Form.Label>
 <Form.Select value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}>
 <option value="">All</option>
 {CATEGORIES.map((item) => <option key={item}>{item}</option>)}
 </Form.Select>
 </Col>
 <Col md={2}>
 <Form.Label>Search</Form.Label>
 <Form.Control value={searchText} onChange={(e) => setSearchText(e.target.value)} placeholder="Risk code/title" />
 </Col>
 <Col md={1} className="d-grid">
 <Button variant="primary" onClick={openCreate} disabled={!canManage}>
 <i className="fa fa-plus me-1" /> Add
 </Button>
 </Col>
 </Row>

 {selectedProjectName && (
 <div className="text-muted small mb-3">Viewing project: {selectedProjectName}</div>
 )}

 {loading ? (
 <div className="text-center py-5"><Spinner animation="border" /></div>
 ) : (
 <div className="table-responsive">
 <Table hover className="align-middle">
 <thead>
 <tr>
 <th>Code</th>
 <th>Risk</th>
 <th>Category</th>
 <th>Score</th>
 <th>Severity</th>
 <th>Status</th>
 <th>Owner</th>
 <th>Target</th>
 <th>Client/PMC</th>
 <th className="text-end">Action</th>
 </tr>
 </thead>
 <tbody>
 {risks.length === 0 ? (
 <tr><td colSpan="10" className="text-center text-muted py-4">No risk entries found</td></tr>
 ) : risks.map((risk) => (
 <tr key={risk._id}>
 <td>{risk.riskCode}</td>
 <td>
 <div className="fw-semibold">{risk.title}</div>
 <div className="small text-muted">{risk.description || "-"}</div>
 </td>
 <td>{risk.category}</td>
 <td>{risk.riskScore}</td>
 <td><Badge bg={severityVariant[risk.severity] || "secondary"}>{risk.severity}</Badge></td>
 <td>{risk.status}</td>
 <td>{risk.owner?.name || "-"}</td>
 <td>{fmtDate(risk.targetClosureDate)}</td>
 <td>
 {risk.clientPmcParametersRequired ? (
 <Badge bg="info">Parameters Required</Badge>
 ) : (
 <Badge bg="success">Received</Badge>
 )}
 </td>
 <td className="text-end">
 <Button size="sm" variant="outline-primary" className="me-2" onClick={() => openEdit(risk)}>
 <i className="fa fa-pen" />
 </Button>
 {canManage && (
 <Button size="sm" variant="outline-danger" onClick={() => handleDelete(risk)}>
 <i className="fa fa-trash" />
 </Button>
 )}
 </td>
 </tr>
 ))}
 </tbody>
 </Table>
 </div>
 )}
 </Card.Body>
 </Card>

 <Modal show={showModal} onHide={() => setShowModal(false)} size="xl" centered>
 <Modal.Header closeButton>
 <Modal.Title>{editingId ? "Edit Risk Entry" : "Add Risk Entry"}</Modal.Title>
 </Modal.Header>
 <Modal.Body>
 <Row className="g-3">
 <Col md={6}>
 <Form.Label>Risk Title</Form.Label>
 <Form.Control value={form.title} onChange={(e) => setF("title", e.target.value)} />
 </Col>
 <Col md={3}>
 <Form.Label>Category</Form.Label>
 <Form.Select value={form.category} onChange={(e) => setF("category", e.target.value)}>
 {CATEGORIES.map((item) => <option key={item}>{item}</option>)}
 </Form.Select>
 </Col>
 <Col md={3}>
 <Form.Label>Status</Form.Label>
 <Form.Select value={form.status} onChange={(e) => setF("status", e.target.value)}>
 {STATUSES.map((item) => <option key={item}>{item}</option>)}
 </Form.Select>
 </Col>
 <Col md={12}>
 <Form.Label>Description</Form.Label>
 <Form.Control as="textarea" rows={2} value={form.description} onChange={(e) => setF("description", e.target.value)} />
 </Col>
 <Col md={2}>
 <Form.Label>Probability</Form.Label>
 <Form.Control type="number" min="1" max="5" value={form.probability} onChange={(e) => setF("probability", e.target.value)} />
 </Col>
 <Col md={2}>
 <Form.Label>Impact</Form.Label>
 <Form.Control type="number" min="1" max="5" value={form.impact} onChange={(e) => setF("impact", e.target.value)} />
 </Col>
 <Col md={2}>
 <Form.Label>Score</Form.Label>
 <Form.Control value={scorePreview} disabled />
 </Col>
 <Col md={3}>
 <Form.Label>Owner</Form.Label>
 <Form.Select value={form.owner} onChange={(e) => setF("owner", e.target.value)}>
 <option value="">Unassigned</option>
 {users.map((item) => <option key={item._id} value={item._id}>{item.name || item.email}</option>)}
 </Form.Select>
 </Col>
 <Col md={3}>
 <Form.Label>Target Closure</Form.Label>
 <Form.Control type="date" value={form.targetClosureDate} onChange={(e) => setF("targetClosureDate", e.target.value)} />
 </Col>
 <Col md={6}>
 <Form.Label>Mitigation Plan</Form.Label>
 <Form.Control as="textarea" rows={2} value={form.mitigationPlan} onChange={(e) => setF("mitigationPlan", e.target.value)} />
 </Col>
 <Col md={6}>
 <Form.Label>Contingency Plan</Form.Label>
 <Form.Control as="textarea" rows={2} value={form.contingencyPlan} onChange={(e) => setF("contingencyPlan", e.target.value)} />
 </Col>
 </Row>

 <hr />
 <div className="d-flex justify-content-between align-items-center mb-2">
 <h6 className="mb-0">Client/PMC Parameters</h6>
 <Form.Check
 type="switch"
 label="Parameters required"
 checked={form.clientPmcParametersRequired}
 onChange={(e) => setF("clientPmcParametersRequired", e.target.checked)}
 />
 </div>
 <Row className="g-3">
 <Col md={4}>
 <Form.Label>Acceptance Criteria</Form.Label>
 <Form.Control value={form.clientPmcParameters.acceptanceCriteria} onChange={(e) => setClientParam("acceptanceCriteria", e.target.value)} />
 </Col>
 <Col md={4}>
 <Form.Label>Escalation Threshold</Form.Label>
 <Form.Control value={form.clientPmcParameters.escalationThreshold} onChange={(e) => setClientParam("escalationThreshold", e.target.value)} />
 </Col>
 <Col md={4}>
 <Form.Label>Response TAT</Form.Label>
 <Form.Control value={form.clientPmcParameters.responseTat} onChange={(e) => setClientParam("responseTat", e.target.value)} />
 </Col>
 <Col md={4}>
 <Form.Label>Approval Authority</Form.Label>
 <Form.Control value={form.clientPmcParameters.approvalAuthority} onChange={(e) => setClientParam("approvalAuthority", e.target.value)} />
 </Col>
 <Col md={4}>
 <Form.Label>Communication Protocol</Form.Label>
 <Form.Control value={form.clientPmcParameters.communicationProtocol} onChange={(e) => setClientParam("communicationProtocol", e.target.value)} />
 </Col>
 <Col md={4}>
 <Form.Label>Remarks</Form.Label>
 <Form.Control value={form.clientPmcParameters.remarks} onChange={(e) => setClientParam("remarks", e.target.value)} />
 </Col>
 </Row>

 <hr />
 <h6>Escalation Matrix</h6>
 <div className="table-responsive">
 <Table bordered size="sm" className="align-middle">
 <thead>
 <tr>
 <th style={{ width: 130 }}>Level</th>
 <th>Trigger</th>
 <th>Owner</th>
 <th>Email</th>
 <th style={{ width: 120 }}>TAT Hours</th>
 <th>Action</th>
 </tr>
 </thead>
 <tbody>
 {form.escalationMatrix.map((row, index) => (
 <tr key={`${row.level}-${index}`}>
 <td>{row.level}</td>
 <td><Form.Control value={row.trigger} onChange={(e) => setEscalation(index, "trigger", e.target.value)} /></td>
 <td><Form.Control value={row.ownerName} onChange={(e) => setEscalation(index, "ownerName", e.target.value)} /></td>
 <td><Form.Control value={row.ownerEmail} onChange={(e) => setEscalation(index, "ownerEmail", e.target.value)} /></td>
 <td><Form.Control type="number" min="0" value={row.responseTimeHours} onChange={(e) => setEscalation(index, "responseTimeHours", e.target.value)} /></td>
 <td><Form.Control value={row.actionRequired} onChange={(e) => setEscalation(index, "actionRequired", e.target.value)} /></td>
 </tr>
 ))}
 </tbody>
 </Table>
 </div>
 </Modal.Body>
 <Modal.Footer>
 <Button variant="light" onClick={() => setShowModal(false)}>Cancel</Button>
 <Button variant="primary" onClick={handleSave} disabled={saving || !canManage}>
 {saving ? "Saving..." : "Save"}
 </Button>
 </Modal.Footer>
 </Modal>
 </>
 );
};

export default RiskRegister;
